/**
 * AML / Qualitas compliance domain types.
 * Maps DB snake_case → camelCase for the app layer.
 */

export type AmlDeclarationStatus =
  | 'draft'
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'rejected'
  | 'needs_update'

export type AmlIncomeRange =
  | '1_800'
  | '801_2000'
  | '2001_4500'
  | '4501_6500'
  | '6501_10000'
  | '10001_plus'

export type AmlFundOrigin = 'salaried' | 'independent_merchant' | 'independent_professional'

export type AmlGender = 'female' | 'male' | 'other' | 'prefer_not_to_say'

/**
 * Closed occupation catalogue. Free text is not accepted: the partner's
 * "Actividad Económica" field is governed by a code catalogue (see
 * `docs/audit/QUALITAS_MAPEO.md` H4), and a text box produces values that
 * cannot be mapped to it. Labels live in i18n under `aml.activity_option.*`.
 */
export type AmlEconomicActivity =
  | 'employee_private'
  | 'employee_public'
  | 'merchant'
  | 'independent_professional'
  | 'health'
  | 'education'
  | 'transport'
  | 'construction'
  | 'agriculture'
  | 'tourism_hospitality'
  | 'technology'
  | 'finance'
  | 'security'
  | 'student'
  | 'retired'
  | 'homemaker'
  | 'unemployed'
  | 'other'

/** Form values before submit — identity may be prefilled from profile/KYC. */
export interface AmlFormValues {
  fullName: string
  phone: string
  dateOfBirth: string
  gender: AmlGender | null
  nationalityCountryId: string | null
  originCountryId: string | null
  /** When nationality is not El Salvador, origin country is required. */
  residenceCountryId: string | null
  incomeRange: AmlIncomeRange | null
  economicActivity: AmlEconomicActivity | null
  fundOrigin: AmlFundOrigin | null
  isPep: boolean | null
  isPoliticalPartyMember: boolean | null
  hasPepFamily: boolean | null
  hasPepAssociate: boolean | null
  hasTaxHavenRelations: boolean | null
  hasIllicitOrJudicialHistory: boolean | null
  disclaimerAccepted: boolean
  disclaimerScrollCompleted: boolean
  /**
   * Express, prior authorization to send this declaration to the insurer,
   * which processes it in Mexico (D.L. 144/2024 Art. 44). It is its own field
   * on purpose: bundling it into `disclaimerAccepted` would make one tap carry
   * two unrelated permissions, which is exactly what Art. 44 forbids.
   */
  crossBorderTransferAccepted: boolean
}

export interface AmlDeclaration {
  id: string
  profileId: string
  fullName: string
  phone: string
  dateOfBirth: string
  gender: AmlGender
  nationalityCountryId: string
  originCountryId: string | null
  residenceCountryId: string
  incomeRange: AmlIncomeRange
  economicActivity: string
  fundOrigin: AmlFundOrigin
  isPep: boolean
  isPoliticalPartyMember: boolean
  hasPepFamily: boolean
  hasPepAssociate: boolean
  hasTaxHavenRelations: boolean
  hasIllicitOrJudicialHistory: boolean
  disclaimerVersion: string
  disclaimerAcceptedAt: string | null
  disclaimerScrollCompleted: boolean
  status: AmlDeclarationStatus
  submittedAt: string | null
  reviewedAt: string | null
  rejectionReason: string | null
  /** When the declaration was handed to the insurer. Null until it is remitted. */
  exportedToPartnerAt: string | null
  createdAt: string
  updatedAt: string
}

/** Identity data Tago already holds; never asked for a second time. */
export type AmlPrefillField =
  | 'fullName'
  | 'phone'
  | 'dateOfBirth'
  | 'gender'
  | 'nationalityCountryId'
  | 'residenceCountryId'

/** Where a prefilled value came from — rendered as provenance, never guessed. */
export type AmlPrefillSource = 'identity_document' | 'profile'

/** Prefill snapshot pulled from profile + approved identity document. */
export interface AmlIdentityPrefill {
  fullName: string | null
  phone: string | null
  dateOfBirth: string | null
  gender: AmlGender | null
  nationalityCountryId: string | null
  residenceCountryId: string | null
  /**
   * Provenance per field. A field with a source is already known, so the screen
   * renders it as a confirmable summary row instead of a blank input.
   */
  sources: Partial<Record<AmlPrefillField, AmlPrefillSource>>
}

export const AML_INCOME_RANGES: readonly AmlIncomeRange[] = [
  '1_800',
  '801_2000',
  '2001_4500',
  '4501_6500',
  '6501_10000',
  '10001_plus',
] as const

export const AML_FUND_ORIGINS: readonly AmlFundOrigin[] = [
  'salaried',
  'independent_merchant',
  'independent_professional',
] as const

export const AML_GENDERS: readonly AmlGender[] = [
  'female',
  'male',
  'other',
  'prefer_not_to_say',
] as const

export const AML_ECONOMIC_ACTIVITIES: readonly AmlEconomicActivity[] = [
  'employee_private',
  'employee_public',
  'merchant',
  'independent_professional',
  'health',
  'education',
  'transport',
  'construction',
  'agriculture',
  'tourism_hospitality',
  'technology',
  'finance',
  'security',
  'student',
  'retired',
  'homemaker',
  'unemployed',
  'other',
] as const

/**
 * Declarations written before the catalogue existed hold free text. Anything
 * outside the closed list reads as "not answered" so the user picks a code
 * instead of shipping unmappable prose to the insurer.
 */
export function toAmlEconomicActivity(
  value: string | null | undefined,
): AmlEconomicActivity | null {
  if (!value) return null
  return (AML_ECONOMIC_ACTIVITIES as readonly string[]).includes(value)
    ? (value as AmlEconomicActivity)
    : null
}

/** Statuses that satisfy "user completed the form + disclaimer". */
const AML_COMPLETED_STATUSES: readonly AmlDeclarationStatus[] = [
  'submitted',
  'under_review',
  'approved',
] as const

/** Host listing hard-block when partner requires OFAC approval first. */
const AML_HOST_READY_STATUSES: readonly AmlDeclarationStatus[] = [
  'under_review',
  'approved',
] as const

export function isAmlFormComplete(status: AmlDeclarationStatus | null | undefined): boolean {
  return !!status && (AML_COMPLETED_STATUSES as readonly string[]).includes(status)
}

/**
 * Insurer error `0284` refuses a politically exposed person through the web
 * service (`docs/audit/QUALITAS_ANALISIS.md`), so a PEP answer is a business
 * outcome — manual underwriting — and never a validation failure. The screen
 * uses this to explain the path forward instead of blocking the form.
 */
export function requiresManualPepReview(
  values: Pick<AmlFormValues, 'isPep' | 'hasPepFamily' | 'hasPepAssociate'>,
): boolean {
  return values.isPep === true || values.hasPepFamily === true || values.hasPepAssociate === true
}

/**
 * How long the insurer gets to object after a declaration reaches them.
 *
 * Quálitas asked for "un período de espera para que Quálitas valide en las
 * listas OFAC antes del registro", and the window starts when the declaration
 * is REMITTED — not when it is written. A clock that starts before they can see
 * anything is not a review period, it is a delay.
 *
 * NULL until they tell us the number, and null means NOBODY WAITS. That is the
 * whole point of leaving it null rather than picking something reasonable: an
 * invented duration becomes policy the moment it ships, and the first person to
 * find out that hosting is gated on a figure nobody agreed to would be a host
 * with a car and no explanation.
 *
 * The plumbing around it is finished and exercised, so turning it on is this one
 * value and nothing else.
 */
export const AML_OFAC_REVIEW_WINDOW_HOURS: number | null = null

export function isAmlHostReady(
  status: AmlDeclarationStatus | null | undefined,
  requireApproval: boolean,
  /**
   * Remittance timestamp and clock, both optional. Omitted, the rule is exactly
   * what it was — so every caller that has not been taught about the window
   * keeps its current behaviour instead of silently tightening.
   */
  exportedToPartnerAt?: string | null,
  now: number = Date.now(),
  /**
   * The window, injectable. Defaults to the agreed value — which is null today.
   *
   * A parameter rather than a bare module read so the waiting behaviour can be
   * PROVEN before anyone turns it on. Plumbing that only runs the day it goes
   * live is plumbing nobody has tested, and this one decides whether a host can
   * publish.
   */
  windowHours: number | null = AML_OFAC_REVIEW_WINDOW_HOURS,
): boolean {
  if (!status) return false
  if (status === 'approved') return true
  // An explicit objection ends it. `needs_update` and `rejected` are never
  // "still waiting"; they are an answer.
  if (status === 'needs_update' || status === 'rejected') return false
  if (requireApproval) return false

  const submitted =
    (AML_HOST_READY_STATUSES as readonly string[]).includes(status) || status === 'submitted'
  if (!submitted) return false

  // No window requested by the caller: the pre-existing rule, unchanged.
  if (exportedToPartnerAt === undefined) return true

  // No window AGREED yet. Until Quálitas gives us the number this waits for
  // nobody — an unset duration is not a reason to hold somebody's listing.
  if (windowHours === null) return true

  // Remitted never: the clock has not started. Deliberately does NOT block —
  // the reason it has not reached them is ours, not the host's, and making
  // somebody wait for our own backlog is how a listing flow dies. It is visible
  // in the roster's "sin remitir" count instead.
  if (!exportedToPartnerAt) return true

  const elapsedHours = (now - new Date(exportedToPartnerAt).getTime()) / 3_600_000
  // NaN from an unparseable timestamp fails OPEN, like every other unknown here.
  if (!Number.isFinite(elapsedHours)) return true
  return elapsedHours >= windowHours
}
